import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import type {MeshNodes} from "@cm/render-nodes"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"
import {geomToMesh, meshToGeom, GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import {clipAndOffsetMeshForDecal} from "#template/geometry-processing/common-geometry-graphs"

const TD = TypeDescriptors

const getMeshDecalDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    computeDummyReifiedMeshData: TD.inlet(TD.Boolean),
    inputCompleteMeshData: TD.inlet(TD.CompleteMeshData),
    offset: TD.inlet(TD.Tuple<[number, number]>()),
    rotation: TD.inlet(TD.Number),
    size: TD.inlet(TD.Tuple<[number, number]>()),
    distance: TD.inlet(TD.Number),
    completeMeshData: TD.outlet(TD.CompleteMeshData),
}

function applyDecalToGraph(graph: MeshNodes.Mesh, offset: [number, number], rotation: number, size: [number, number], distance: number): MeshNodes.Mesh {
    const ctx = new GeomBuilderContext()
    const clipped = clipAndOffsetMeshForDecal(meshToGeom(ctx, graph, 1), offset, rotation, size, distance)
    // force all material IDs to 0 (assumed to be the only material slot for decals)
    clipped.materialID = clipped.materialID.constInt(0)
    return geomToMesh(clipped)
}

export class GetMeshDecal implements NodeClassImpl<typeof getMeshDecalDescriptor, typeof GetMeshDecal> {
    static descriptor = getMeshDecalDescriptor
    static uniqueName = "GetMeshDecal"
    sceneManager!: Inlet<ISceneManager>
    computeDummyReifiedMeshData!: Inlet<boolean>
    inputCompleteMeshData!: Inlet<CompleteMeshData>
    offset!: Inlet<[number, number]>
    rotation!: Inlet<number>
    size!: Inlet<[number, number]>
    distance!: Inlet<number>
    completeMeshData!: Outlet<CompleteMeshData>

    private pending: Subscription | null = null

    run() {
        this.cleanup()

        if (
            this.sceneManager === NotReady ||
            this.computeDummyReifiedMeshData === NotReady ||
            this.inputCompleteMeshData === NotReady ||
            this.offset === NotReady ||
            this.rotation === NotReady ||
            this.size === NotReady ||
            this.distance === NotReady
        ) {
            this.completeMeshData.emitIfChanged(NotReady)
            return
        }

        const {sceneManager, inputCompleteMeshData: inputMeshData, offset, rotation, size, distance} = this
        const displayGeometryGraph = applyDecalToGraph(inputMeshData.abstract.displayGeometryGraph, offset, rotation, size, distance)
        const renderGeometryGraph = applyDecalToGraph(inputMeshData.abstract.renderGeometryGraph, offset, rotation, size, distance)

        this.pending = this.sceneManager.addTask({
            description: `clipAndOffsetMeshForDecal${this.computeDummyReifiedMeshData ? "Dummy" : ""}(${this.inputCompleteMeshData.reified.contentHash})`,
            task: defer(() =>
                this.computeDummyReifiedMeshData
                    ? sceneManager.evaluateMesh(
                          {
                              type: "empty",
                          },
                          {},
                      )
                    : sceneManager.clipAndOffsetMeshForDecal(inputMeshData.reified, offset, rotation, size, distance),
            ).pipe(
                map((reified) => {
                    this.completeMeshData.emitIfChanged({
                        reified,
                        abstract: {
                            contentHash: reified.contentHash,
                            displayGeometryGraph,
                            renderGeometryGraph,
                            displayGeometryGraphResources: inputMeshData.abstract.displayGeometryGraphResources,
                        },
                    })
                }),
            ),
            critical: true,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
